import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import CakeIcon from "@mui/icons-material/Cake";
import dayjs from "dayjs";
import { SERVER_URL } from "../../constants";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// dayjs().day() is 0=Sun..6=Sat; we lay the grid out Monday-first.
function mondayOnOrBefore(d) {
  const dow = d.day();
  return d.subtract(dow === 0 ? 6 : dow - 1, "day");
}
function sundayOnOrAfter(d) {
  const dow = d.day();
  return dow === 0 ? d : d.add(7 - dow, "day");
}

// A student's birthday in the given calendar year (guards against a Feb-29 DOB
// falling in a non-leap year).
function birthdayInYear(dob, year) {
  let b = dayjs(`${year}-${String(dob.month() + 1).padStart(2, "0")}-${String(dob.date()).padStart(2, "0")}`);
  if (!b.isValid() && dob.month() === 1 && dob.date() === 29) {
    b = dayjs(`${year}-02-28`);
  }
  return b.isValid() ? b : null;
}

export default function BirthdayCalendarPage({ selectedYear, onSelectStudent }) {
  const year = Number(selectedYear);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Open on the current month, pinned to the selected year (camp season).
  const [month, setMonth] = useState(() =>
    dayjs().set("year", year || dayjs().year()).startOf("month")
  );

  useEffect(() => {
    setMonth(dayjs().set("year", year || dayjs().year()).startOf("month"));
  }, [year]);

  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const token = sessionStorage.getItem("bearer");
    fetch(`${SERVER_URL}api/residentBirthdays/${year}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.status === 204) return [];
        if (res.status === 404) {
          throw new Error(
            "Birthdays feed not available yet — the backend endpoint " +
              "(api/residentBirthdays) is pending. See backend ticket."
          );
        }
        if (!res.ok) throw new Error("Failed to load birthdays");
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setStudents(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load students");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year]);

  // Map "YYYY-MM-DD" -> [{ id, name, campus, age }] for every resident student
  // whose birthday falls within their stay this year. One row per (student,
  // stay); a student appearing in several stays is de-duped per day.
  const { byDate: birthdaysByDate, withDob } = useMemo(() => {
    const map = new Map();
    const perDaySeen = new Map(); // "date" -> Set(studentId)
    let dobCount = 0;
    (students || []).forEach((s) => {
      const dob = dayjs(s.studentDob);
      if (!s.studentDob || !dob.isValid()) return;
      dobCount += 1;
      const arrival = dayjs(s.arrivalDate);
      const departure = dayjs(s.departureDate);
      if (!arrival.isValid() || !departure.isValid()) return; // no stay -> not resident

      const bday = birthdayInYear(dob, year);
      if (!bday) return;
      // Resident on their birthday = birthday within the stay window (inclusive).
      if (bday.isBefore(arrival, "day") || bday.isAfter(departure, "day")) return;

      const key = bday.format("YYYY-MM-DD");
      const id = s.id ?? s.studentId;
      if (!perDaySeen.has(key)) perDaySeen.set(key, new Set());
      if (perDaySeen.get(key).has(id)) return; // same student, another stay
      perDaySeen.get(key).add(id);

      if (!map.has(key)) map.set(key, []);
      map.get(key).push({
        id,
        name: `${s.studentName || ""} ${s.studentSurname || ""}`.trim() || `#${id}`,
        campus: s.campus || "",
        age: dob.year() ? year - dob.year() : null,
      });
    });
    // Stable name order within a day.
    map.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return { byDate: map, withDob: dobCount };
  }, [students, year]);

  // The feed sometimes omits date-of-birth (studentsBasicYear historically did):
  // surface that rather than showing a silently-empty calendar.
  const missingDob = !loading && !error && students.length > 0 && withDob === 0;

  const monthCount = useMemo(() => {
    let n = 0;
    birthdaysByDate.forEach((list, key) => {
      if (dayjs(key).isSame(month, "month")) n += list.length;
    });
    return n;
  }, [birthdaysByDate, month]);

  const gridDays = useMemo(() => {
    const start = mondayOnOrBefore(month.startOf("month"));
    const end = sundayOnOrAfter(month.endOf("month"));
    const days = [];
    for (let cur = start; cur.valueOf() <= end.valueOf(); cur = cur.add(1, "day")) {
      days.push(cur);
    }
    return days;
  }, [month]);

  const goPrev = useCallback(() => setMonth((m) => m.subtract(1, "month")), []);
  const goNext = useCallback(() => setMonth((m) => m.add(1, "month")), []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", boxSizing: "border-box" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <CakeIcon color="secondary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Birthdays
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={goPrev} aria-label="Previous month">
          <NavigateBefore />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: 180, textAlign: "center" }}>
          {month.format("MMMM YYYY")}
        </Typography>
        <IconButton onClick={goNext} aria-label="Next month">
          <NavigateNext />
        </IconButton>
        <Chip
          size="small"
          color={monthCount ? "secondary" : "default"}
          label={`${monthCount} this month`}
          sx={{ ml: 1 }}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Resident students whose birthday falls during their stay in {year}.
      </Typography>

      {missingDob && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loaded {students.length} records, but none included a date of birth —
          so no birthdays can be shown. The feed needs a <code>studentDob</code>
          {" "}field (see backend ticket).
        </Alert>
      )}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 6, justifyContent: "center" }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading students…</Typography>
        </Stack>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {/* Weekday header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
              mb: 1,
            }}
          >
            {WEEKDAYS.map((d) => (
              <Typography
                key={d}
                variant="subtitle2"
                align="center"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                {d}
              </Typography>
            ))}
          </Box>

          {/* Day grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridAutoRows: "1fr",
              gap: 1,
            }}
          >
            {gridDays.map((day) => {
              const inMonth = day.isSame(month, "month");
              const key = day.format("YYYY-MM-DD");
              const list = birthdaysByDate.get(key) || [];
              const isToday = day.isSame(dayjs(), "day");
              const hasBdays = list.length > 0;
              return (
                <Box
                  key={key}
                  sx={{
                    minHeight: { xs: 72, md: 110 },
                    border: "1px solid",
                    borderColor: isToday ? "primary.main" : "divider",
                    borderRadius: 1.5,
                    p: 0.75,
                    bgcolor: !inMonth
                      ? "action.hover"
                      : hasBdays
                      ? "secondary.light"
                      : "background.paper",
                    opacity: inMonth ? 1 : 0.5,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: isToday ? 800 : 600, color: "text.secondary", mb: 0.25 }}
                  >
                    {day.date()}
                  </Typography>
                  <Stack spacing={0.25} sx={{ overflow: "auto" }}>
                    {list.map((b) => (
                      <Tooltip
                        key={`${b.id}-${b.name}`}
                        title={
                          `${b.name}${b.age != null ? ` — turns ${b.age}` : ""}` +
                          (b.campus ? ` · ${b.campus}` : "")
                        }
                        arrow
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          onClick={() => onSelectStudent?.(b.id)}
                          sx={{
                            cursor: onSelectStudent ? "pointer" : "default",
                            "&:hover": onSelectStudent ? { textDecoration: "underline" } : undefined,
                          }}
                        >
                          <CakeIcon sx={{ fontSize: 12, color: "secondary.dark", flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontWeight: 600, lineHeight: 1.2 }}
                          >
                            {b.name}
                            {b.campus ? (
                              <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                                {" "}· {b.campus}
                              </Box>
                            ) : null}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
